// 전투 카메라 연출(컷): 이벤트마다 짧은 카메라 동작을 기본 추적 구도 위에 겹친다.
// 규칙(AV-SPEC·COMBAT-FEEL 7장): 지속 흔들림·떨림 없음. 컷마다 한 방향으로만 천천히 돌고(궤도), 들어오고 나갈 때 부드럽게 섞는다.
// 피치·요 변화는 작게 둔다(입자·바닥 효과가 기본 피치 28°에 맞춰져 있다). 프레임마다 새 객체를 만들지 않는다.

/** 컷 하나의 모양. 값은 기본 구도에 더하는 변화량(강도 1 기준) */
export interface ShotSpec {
  /** 이름(QA·디버그 표시) */
  readonly id: string;
  /** 전체 길이(초), 들어오기·나가기 시간(초) */
  readonly dur: number;
  readonly fadeIn: number;
  readonly fadeOut: number;
  /** 높을수록 우선. 진행 중인 컷보다 낮으면 무시한다 */
  readonly prio: number;
  /** 거리 배율(1 = 그대로, 0.6 = 다가감) */
  readonly dist: number;
  /** 피치 변화(도, 음수 = 낮은 앵글) */
  readonly pitch: number;
  /** 궤도: 시작·끝 요(도). 컷 동안 한 방향으로만 움직인다 */
  readonly yaw0: number;
  readonly yaw1: number;
  /** 화각 변화(도) */
  readonly fov: number;
  /** 초점 지점 쪽으로 시선을 옮기는 비율(0 = 영웅, 1 = 지점) */
  readonly focus: number;
  /** 바라보는 높이(m): 낮은 앵글에서 얼굴·상체가 보이게 */
  readonly lookY: number;
}

const shot = (s: ShotSpec): ShotSpec => s;

/** 컷 표(COMBAT-FEEL 7.1). 게임이 멈추거나 무적인 구간(보스 등장·각성·보스 처치)만 길게 쓴다 */
export const SHOTS = {
  /** 보스 등장(1.5초 무적): 보스 쪽으로 낮게 다가가며 천천히 돈다 */
  bossIntro: shot({ id: 'bossIntro', dur: 2.0, fadeIn: 0.45, fadeOut: 0.6, prio: 4, dist: 0.58, pitch: -10, yaw0: 3, yaw1: 13, fov: -6, focus: 0.7, lookY: 2.6 }),
  /**
   * 각성 모으기~컷인(약 1초, 세계 시간 0.2배): 영웅 쪽으로 다가가며 돈다.
   * 몹이 영웅 둘레로 끌려와 카메라 쪽(남쪽)에도 모이므로 너무 낮고 가까우면 몹 몸통이 화면을 가린다(2026-10-14 실화면).
   * 거리 0.74·피치 -4°로 몹 머리 위에서 영웅과 끌려오는 무리를 함께 담는다.
   */
  awakenGather: shot({ id: 'awakenGather', dur: 1.05, fadeIn: 0.3, fadeOut: 0, prio: 5, dist: 0.74, pitch: -4, yaw0: 0, yaw1: -9, fov: -3, focus: 0, lookY: 0.9 }),
  /** 각성 난무(0.9초 + 회복): 뒤로 빠지며 같은 방향으로 계속 돈다 */
  awakenBurst: shot({ id: 'awakenBurst', dur: 1.4, fadeIn: 0.4, fadeOut: 0.6, prio: 5, dist: 1.2, pitch: 5, yaw0: -8, yaw1: -14, fov: 5, focus: 0, lookY: 0 }),
  /** 피의 돌진: 짧게 당기고 화각을 넓혀 속도감 */
  skillDash: shot({ id: 'skillDash', dur: 0.5, fadeIn: 0.08, fadeOut: 0.3, prio: 1, dist: 0.9, pitch: 0, yaw0: 0, yaw1: 0, fov: 6, focus: 0, lookY: 0 }),
  /** 칼날 폭풍: 범위가 보이게 살짝 높고 멀리 */
  skillStorm: shot({ id: 'skillStorm', dur: 0.95, fadeIn: 0.2, fadeOut: 0.4, prio: 1, dist: 1.14, pitch: 4, yaw0: 0, yaw1: 0, fov: 2, focus: 0, lookY: 0 }),
  /** 붉은 낙화: 도약을 낮은 앵글로 */
  skillLeap: shot({ id: 'skillLeap', dur: 0.75, fadeIn: 0.15, fadeOut: 0.35, prio: 1, dist: 0.86, pitch: -6, yaw0: 0, yaw1: 0, fov: 0, focus: 0, lookY: 0.8 }),
  /** 천공 올려베기(스킬 5, 타임라인 카메라 큐 punch_mid): 시선을 살짝 올려 띄운 몹이 화면에 남게, 짧게 */
  skillLauncher: shot({ id: 'skillLauncher', dur: 0.6, fadeIn: 0.08, fadeOut: 0.3, prio: 1, dist: 0.95, pitch: 2, yaw0: 0, yaw1: 0, fov: 2, focus: 0, lookY: 1.2 }),
  /** 진홍 초승달(스킬 4): 길이 8m 전방이 보이게 아주 조금 물러나며 시선만 앞쪽으로(지속 흔들림·줌 펄스 없음, 강도 설정을 따른다) */
  skillCrescent: shot({ id: 'skillCrescent', dur: 0.55, fadeIn: 0.1, fadeOut: 0.3, prio: 1, dist: 1.05, pitch: 0, yaw0: 0, yaw1: 0, fov: 1, focus: 0.2, lookY: 0 }),
  /** 한 동작 대량 처치: 튕겨 나가는 몹이 보이게 잠깐 넓게 */
  massKill: shot({ id: 'massKill', dur: 0.8, fadeIn: 0.1, fadeOut: 0.5, prio: 2, dist: 1.12, pitch: 2, yaw0: 0, yaw1: 0, fov: 3, focus: 0, lookY: 0 }),
  /** 웨이브 시작: 입구가 보이게 잠깐 넓게 */
  wave: shot({ id: 'wave', dur: 1.1, fadeIn: 0.3, fadeOut: 0.6, prio: 1, dist: 1.12, pitch: 2, yaw0: 0, yaw1: 0, fov: 0, focus: 0, lookY: 0 }),
  /** 방 클리어: 영웅에게 다가가 낮게 반 바퀴 일부를 돈다 */
  roomClear: shot({ id: 'roomClear', dur: 1.6, fadeIn: 0.35, fadeOut: 0.7, prio: 2, dist: 0.8, pitch: -6, yaw0: 0, yaw1: 7, fov: -2, focus: 0, lookY: 0.9 }),
  /** 전직(C10-5, 마을 교관 앞): 영웅에게 천천히 다가가며 낮은 앵글로 반 바퀴를 돈다. 컷 글자(3.2초) 동안 영웅이 화면 가운데 아래에 남게 시선을 조금 위로 */
  jobChange: shot({ id: 'jobChange', dur: 3.0, fadeIn: 0.6, fadeOut: 0.8, prio: 7, dist: 0.66, pitch: -7, yaw0: 0, yaw1: 14, fov: -5, focus: 0, lookY: 1.5 }),
  /** 보스 처치(쓰러지는 1.5초 + 결과 전): 보스 쪽 낮은 앵글로 천천히 돈다 */
  bossDown: shot({ id: 'bossDown', dur: 2.4, fadeIn: 0.4, fadeOut: 0.6, prio: 6, dist: 0.62, pitch: -9, yaw0: 0, yaw1: 14, fov: -4, focus: 0.65, lookY: 2.2 }),
} as const;

export type ShotId = keyof typeof SHOTS;

/** 합쳐진 카메라 변화량(매 프레임 같은 객체를 다시 쓴다) */
export interface CinePose {
  dist: number;
  pitch: number;
  yaw: number;
  fov: number;
  focus: number;
  fx: number;
  fz: number;
  lookY: number;
}

/** 0..1 부드러운 곡선 */
function smooth(x: number): number {
  const t = x <= 0 ? 0 : x >= 1 ? 1 : x;
  return t * t * (3 - 2 * t);
}

/** 프레임 간격과 무관한 지수 감쇠 계수 */
function follow(sharpness: number, dt: number): number {
  return 1 - Math.exp(-sharpness * dt);
}

/** 컷이 끊기거나 바뀔 때 튀지 않게 실제 적용값이 목표값을 따라가는 속도 */
const BLEND_SHARPNESS = 9;

export class CineDirector {
  /** 강도(0 = 끔). 설정 화면의 전투 연출 */
  intensity = 1;
  private cur: ShotSpec | null = null;
  private t = 0;
  private px = 0;
  private pz = 0;
  /** 컷을 시작할 때의 적용값: 새 컷은 여기서 이어 들어간다(컷이 바뀌어도 튀지 않음) */
  private readonly from: CinePose = { dist: 1, pitch: 0, yaw: 0, fov: 0, focus: 0, fx: 0, fz: 0, lookY: 0 };
  /** 목표값과 실제 적용값 */
  private readonly goal: CinePose = { dist: 1, pitch: 0, yaw: 0, fov: 0, focus: 0, fx: 0, fz: 0, lookY: 0 };
  readonly pose: CinePose = { dist: 1, pitch: 0, yaw: 0, fov: 0, focus: 0, fx: 0, fz: 0, lookY: 0 };

  /** 진행 중인 컷 이름(없으면 '') */
  get active(): string {
    return this.cur ? this.cur.id : '';
  }

  /** 컷을 시작한다. 끔이거나 더 중요한 컷이 진행 중이면 false */
  play(spec: ShotSpec, fx = 0, fz = 0): boolean {
    if (this.intensity <= 0) return false;
    const c = this.cur;
    if (c && c.prio > spec.prio && this.t < c.dur - c.fadeOut) return false;
    this.cur = spec;
    this.t = 0;
    this.px = fx;
    this.pz = fz;
    const f = this.from, p = this.pose;
    f.dist = p.dist; f.pitch = p.pitch; f.yaw = p.yaw; f.fov = p.fov; f.focus = p.focus; f.lookY = p.lookY;
    return true;
  }

  /** 장면 전환: 컷을 끊고 즉시 기본 구도로 돌린다 */
  reset(): void {
    this.cur = null;
    this.t = 0;
    const g = this.goal, p = this.pose;
    g.dist = p.dist = 1; g.pitch = p.pitch = 0; g.yaw = p.yaw = 0; g.fov = p.fov = 0; g.focus = p.focus = 0; g.lookY = p.lookY = 0;
  }

  /** dt(초, 실제 시간: 히트스톱 중에도 흐른다) 뒤의 변화량 */
  update(dt: number): CinePose {
    const g = this.goal;
    const c = this.cur;
    if (c) {
      this.t += dt;
      if (this.t >= c.dur) this.cur = null;
    }
    const s = this.cur;
    if (s && this.intensity > 0) {
      const t = this.t;
      // 들어오기: 시작 시점 적용값 → 컷 값, 나가기: 컷 값 → 기본 구도
      const a = s.fadeIn > 0 ? smooth(t / s.fadeIn) : 1;
      const wOut = s.fadeOut > 0 ? smooth((s.dur - t) / s.fadeOut) : 1;
      const k = this.intensity * wOut;
      const f = this.from;
      const mix = (from: number, full: number, base: number): number => {
        const v = base + (full - base) * k;
        return from + (v - from) * a;
      };
      g.dist = mix(f.dist, s.dist, 1);
      g.pitch = mix(f.pitch, s.pitch, 0);
      g.yaw = mix(f.yaw, s.yaw0 + (s.yaw1 - s.yaw0) * smooth(t / s.dur), 0);
      g.fov = mix(f.fov, s.fov, 0);
      g.focus = Math.min(1, mix(f.focus, s.focus, 0));
      g.lookY = mix(f.lookY, s.lookY, 0);
      g.fx = this.px;
      g.fz = this.pz;
    } else {
      g.dist = 1; g.pitch = 0; g.yaw = 0; g.fov = 0; g.focus = 0; g.lookY = 0;
    }
    const k = follow(BLEND_SHARPNESS, dt);
    const p = this.pose;
    p.dist += (g.dist - p.dist) * k;
    p.pitch += (g.pitch - p.pitch) * k;
    p.yaw += (g.yaw - p.yaw) * k;
    p.fov += (g.fov - p.fov) * k;
    p.focus += (g.focus - p.focus) * k;
    p.lookY += (g.lookY - p.lookY) * k;
    // 초점 지점은 새 컷이 오면 바로 바꾼다(가중치가 부드럽게 오르므로 튀지 않는다)
    p.fx = g.fx;
    p.fz = g.fz;
    return p;
  }
}
